/**
 * Enhanced Data Management Page - Premium tabbed view for Locations, Vehicles, and Jobs
 * Features: Modern design, optimal spacing, professional layout, smooth animations
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Snackbar,
  Chip,
  IconButton,
  Card,
  CardContent,
  LinearProgress,
  Fade,
  Zoom,
  Avatar,
  Stack,
  Badge,
  Tooltip,
  Container,
} from '@mui/material';
import { Divider } from '@mui/material';
import {
  LocationOn,
  DirectionsCar,
  Work,
  Upload,
  Download,
  Add,
  Analytics as AnalyticsIcon,
  Speed as SpeedIcon,
  Insights as InsightsIcon,
  Edit,
  Delete,
  Visibility,
  ContentCopy,
} from '@mui/icons-material';
import { AdvancedDataTable } from '../components/tables/AdvancedDataTable';
import { BulkImportDialog } from '../components/bulk/BulkImportDialog';
import BulkExportDialog from '../components/bulk/BulkExportDialog';
import { ProfessionalLayout } from '../components/layout/ProfessionalLayout';
import { DataMigrationDialog } from '../components/bulk/DataMigrationDialog';
import { EnhancedErrorHandler } from '../components/common/EnhancedErrorHandler';
import { GuidedWorkflowTooltips } from '../components/common/GuidedWorkflowTooltips';
import { ProgressiveDisclosure } from '../components/common/ProgressiveDisclosure';
import { useVRPData } from '../contexts/VRPDataContext';
import { useLocations } from '../hooks/useLocations';
import { useVehicles } from '../hooks/useVehicles';
import { useJobs } from '../hooks/useJobs';
import { apiClient } from '../services/api';
import { formatDuration } from '../utils/timeFormat';
import type { Location, Vehicle, Job } from '../types/vrp';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`enhanced-tabpanel-${index}`}
      aria-labelledby={`enhanced-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Fade in={value === index} timeout={300}>
          <Box sx={{ p: 1 }}>
            {children}
          </Box>
        </Fade>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `enhanced-tab-${index}`,
    'aria-controls': `enhanced-tabpanel-${index}`,
  };
}

// Enhanced theme with professional color palette
const theme = {
  primary: '#1976d2',
  primaryDark: '#1565c0',
  secondary: '#14B8A6',
  accent: '#f59e0b',
  success: '#16a34a',
  warning: '#f59e0b',
  error: '#dc2626',
  background: '#fafafa',
  surface: '#ffffff',
  surfaceVariant: '#f8fafc',
  gradients: {
    primary: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    secondary: 'linear-gradient(135deg, #14B8A6 0%, #0f766e 100%)',
    accent: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    success: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
  },
  shadows: {
    card: '0 4px 20px rgba(0, 0, 0, 0.08)',
    elevated: '0 8px 32px rgba(0, 0, 0, 0.12)',
    float: '0 12px 40px rgba(0, 0, 0, 0.15)',
  },
};

// Comprehensive location columns matching sidebar structure
const LOCATION_COLUMNS = [
  {
    id: 'id',
    label: 'ID',
    dataKey: 'id' as keyof Location,
    sortable: true,
    width: 80,
    align: 'center' as const,
  },
  {
    id: 'name',
    label: 'Location Name',
    dataKey: 'name' as keyof Location,
    sortable: true,
    filterable: true,
    width: 200,
    renderCell: (location: Location) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ 
          width: 28, 
          height: 28, 
          bgcolor: location.cluster_id ? theme.primary : theme.secondary,
          fontSize: '0.75rem'
        }}>
          <LocationOn fontSize="small" />
        </Avatar>
        <Box>
          <Typography variant="body2" fontWeight="600" sx={{ lineHeight: 1.2 }}>
            {location.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {location.location_type?.toUpperCase() || 'CUSTOMER'}
          </Typography>
        </Box>
      </Box>
    ),
  },
  {
    id: 'description',
    label: 'Description',
    dataKey: 'description' as keyof Location,
    sortable: true,
    filterable: true,
    width: 200,
  },
  {
    id: 'location_type',
    label: 'Type',
    dataKey: 'location_type' as keyof Location,
    sortable: true,
    filterable: true,
    width: 100,
    renderCell: (location: Location) => (
      <Chip
        label={location.location_type || 'customer'}
        size="small"
        color={location.location_type === 'depot' ? 'primary' : 'default'}
        variant={location.location_type === 'depot' ? 'filled' : 'outlined'}
        sx={{ fontWeight: 500, textTransform: 'capitalize' }}
      />
    ),
  },
  {
    id: 'address',
    label: 'Address',
    dataKey: 'address' as keyof Location,
    filterable: true,
    width: 250,
  },
  {
    id: 'location_lat',
    label: 'Latitude',
    dataKey: 'location_lat' as keyof Location,
    sortable: true,
    width: 120,
    format: (value: number) => value ? value.toFixed(6) : 'N/A',
  },
  {
    id: 'location_lon',
    label: 'Longitude',
    dataKey: 'location_lon' as keyof Location,
    sortable: true,
    width: 120,
    format: (value: number) => value ? value.toFixed(6) : 'N/A',
  },
  {
    id: 'cluster_id',
    label: 'Cluster ID',
    dataKey: 'cluster_id' as keyof Location,
    sortable: true,
    width: 100,
  },
  {
    id: 'operating_hours',
    label: 'Operating Hours',
    dataKey: 'operating_hours' as keyof Location,
    width: 150,
  },
  {
    id: 'contact_info',
    label: 'Contact Info',
    dataKey: 'contact_info' as keyof Location,
    filterable: true,
    width: 180,
  },
  {
    id: 'job_count',
    label: 'Jobs',
    dataKey: 'job_count' as keyof Location,
    sortable: true,
    width: 80,
    renderCell: (location: Location) => (
      <Chip
        label={location.job_count || 0}
        size="small"
        color={location.job_count && location.job_count > 0 ? 'success' : 'default'}
      />
    ),
  },
  {
    id: 'vehicle_count',
    label: 'Vehicles',
    dataKey: 'vehicle_count' as keyof Location,
    sortable: true,
    width: 80,
    renderCell: (location: Location) => (
      <Chip
        label={location.vehicle_count || 0}
        size="small"
        color={location.vehicle_count && location.vehicle_count > 0 ? 'info' : 'default'}
      />
    ),
  },
  {
    id: 'project_id',
    label: 'Project ID',
    dataKey: 'project_id' as keyof Location,
    sortable: true,
    width: 100,
  },
  {
    id: 'created_at',
    label: 'Created',
    dataKey: 'created_at' as keyof Location,
    sortable: true,
    width: 140,
    format: (value: string) => value ? new Date(value).toLocaleDateString() : 'N/A',
  },
  {
    id: 'updated_at',
    label: 'Updated',
    dataKey: 'updated_at' as keyof Location,
    sortable: true,
    width: 140,
    format: (value: string) => value ? new Date(value).toLocaleDateString() : 'N/A',
  },
];

const VEHICLE_COLUMNS = [
  {
    id: 'id',
    label: 'ID',
    dataKey: 'id' as keyof Vehicle,
    sortable: true,
    width: 80,
    align: 'center' as const,
  },
  {
    id: 'description',
    label: 'Vehicle Name',
    dataKey: 'description' as keyof Vehicle,
    sortable: true,
    filterable: true,
    width: 180,
    renderCell: (vehicle: Vehicle) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ 
          width: 28, 
          height: 28, 
          bgcolor: vehicle.profile === 'driving' ? theme.primary : 
                   vehicle.profile === 'cycling' ? theme.success : theme.accent,
          fontSize: '0.75rem'
        }}>
          <DirectionsCar fontSize="small" />
        </Avatar>
        <Typography variant="body2" fontWeight="600">
          {vehicle.description}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'profile',
    label: 'Profile',
    dataKey: 'profile' as keyof Vehicle,
    sortable: true,
    filterable: true,
    width: 100,
    renderCell: (vehicle: Vehicle) => (
      <Chip
        label={vehicle.profile || 'driving'}
        size="small"
        color={vehicle.profile === 'driving' ? 'primary' : 
               vehicle.profile === 'cycling' ? 'success' : 'warning'}
        variant="filled"
        sx={{ fontWeight: 500, textTransform: 'capitalize' }}
      />
    ),
  },
  {
    id: 'start_location_id',
    label: 'Start Location ID',
    dataKey: 'start_location_id' as keyof Vehicle,
    sortable: true,
    width: 130,
  },
  {
    id: 'end_location_id',
    label: 'End Location ID',
    dataKey: 'end_location_id' as keyof Vehicle,
    sortable: true,
    width: 130,
  },
  {
    id: 'start_lat',
    label: 'Start Latitude',
    dataKey: 'start_lat' as keyof Vehicle,
    sortable: true,
    width: 120,
    format: (value: number) => value ? value.toFixed(6) : 'N/A',
  },
  {
    id: 'start_lon',
    label: 'Start Longitude',
    dataKey: 'start_lon' as keyof Vehicle,
    sortable: true,
    width: 120,
    format: (value: number) => value ? value.toFixed(6) : 'N/A',
  },
  {
    id: 'end_lat',
    label: 'End Latitude',
    dataKey: 'end_lat' as keyof Vehicle,
    sortable: true,
    width: 120,
    format: (value: number) => value ? value.toFixed(6) : 'N/A',
  },
  {
    id: 'end_lon',
    label: 'End Longitude',
    dataKey: 'end_lon' as keyof Vehicle,
    sortable: true,
    width: 120,
    format: (value: number) => value ? value.toFixed(6) : 'N/A',
  },
  {
    id: 'capacity',
    label: 'Capacity',
    dataKey: (row: Vehicle) => Array.isArray(row.capacity) ? row.capacity.join(', ') : String(row.capacity),
    sortable: true,
    width: 120,
    renderCell: (vehicle: Vehicle) => (
      <Chip
        label={Array.isArray(vehicle.capacity) ? vehicle.capacity.join(' / ') : String(vehicle.capacity)}
        size="small"
        variant="outlined"
        color="info"
        sx={{ fontFamily: 'monospace', fontWeight: 500 }}
      />
    ),
  },
  {
    id: 'skills',
    label: 'Skills',
    dataKey: (row: Vehicle) => 
      row.skills && row.skills.length > 0 
        ? row.skills.join(', ')
        : 'None',
    width: 150,
  },
  {
    id: 'max_travel_time',
    label: 'Max Travel Time',
    dataKey: 'max_travel_time' as keyof Vehicle,
    sortable: true,
    width: 140,
    format: (value: number) => value ? `${Math.round(value)} min` : 'No limit',
  },
  {
    id: 'max_distance',
    label: 'Max Distance',
    dataKey: 'max_distance' as keyof Vehicle,
    sortable: true,
    width: 120,
    format: (value: number) => value ? `${(value / 1000).toFixed(1)} km` : 'No limit',
  },
  {
    id: 'max_tasks',
    label: 'Max Tasks',
    dataKey: 'max_tasks' as keyof Vehicle,
    sortable: true,
    width: 100,
    format: (value: number) => value ? String(value) : 'No limit',
  },
  {
    id: 'speed_factor',
    label: 'Speed Factor',
    dataKey: 'speed_factor' as keyof Vehicle,
    sortable: true,
    width: 120,
    format: (value: number) => value ? `${value}x` : '1.0x',
  },
  {
    id: 'tw_start',
    label: 'Time Window Start',
    dataKey: 'tw_start' as keyof Vehicle,
    sortable: true,
    width: 140,
    format: (value: number) => {
      if (value === undefined || value === null) return 'All day';
      const hours = Math.floor(value / 3600);
      const minutes = Math.floor((value % 3600) / 60);
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    },
  },
  {
    id: 'tw_end',
    label: 'Time Window End',
    dataKey: 'tw_end' as keyof Vehicle,
    sortable: true,
    width: 140,
    format: (value: number) => {
      if (value === undefined || value === null) return 'All day';
      const hours = Math.floor(value / 3600);
      const minutes = Math.floor((value % 3600) / 60);
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    },
  },
  {
    id: 'cost_fixed',
    label: 'Fixed Cost',
    dataKey: 'cost_fixed' as keyof Vehicle,
    sortable: true,
    width: 100,
    format: (value: number) => value ? `R${value}` : 'R0',
  },
  {
    id: 'cost_per_hour',
    label: 'Cost per Hour',
    dataKey: 'cost_per_hour' as keyof Vehicle,
    sortable: true,
    width: 120,
    format: (value: number) => value ? `R${value}/h` : 'R0/h',
  },
  {
    id: 'cost_per_km',
    label: 'Cost per KM',
    dataKey: 'cost_per_km' as keyof Vehicle,
    sortable: true,
    width: 110,
    format: (value: number) => value ? `R${value}/km` : 'R0/km',
  },
  {
    id: 'project_id',
    label: 'Project ID',
    dataKey: 'project_id' as keyof Vehicle,
    sortable: true,
    width: 100,
  },
  {
    id: 'created_at',
    label: 'Created',
    dataKey: 'created_at' as keyof Vehicle,
    sortable: true,
    width: 140,
    format: (value: string) => value ? new Date(value).toLocaleDateString() : 'N/A',
  },
  {
    id: 'updated_at',
    label: 'Updated',
    dataKey: 'updated_at' as keyof Vehicle,
    sortable: true,
    width: 140,
    format: (value: string) => value ? new Date(value).toLocaleDateString() : 'N/A',
  },
];

const JOB_COLUMNS = [
  {
    id: 'id',
    label: 'ID',
    dataKey: 'id' as keyof Job,
    sortable: true,
    width: 80,
    align: 'center' as const,
  },
  {
    id: 'description',
    label: 'Job Description',
    dataKey: 'description' as keyof Job,
    sortable: true,
    filterable: true,
    width: 200,
    renderCell: (job: Job) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ 
          width: 28, 
          height: 28, 
          bgcolor: job.priority >= 4 ? theme.error : 
                   job.priority >= 2 ? theme.warning : theme.primary,
          fontSize: '0.75rem'
        }}>
          <Work fontSize="small" />
        </Avatar>
        <Typography variant="body2" fontWeight="600" title={job.description}>
          {job.description}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'location_id',
    label: 'Location ID',
    dataKey: 'location_id' as keyof Job,
    sortable: true,
    width: 100,
  },
  {
    id: 'location_lat',
    label: 'Latitude',
    dataKey: 'location_lat' as keyof Job,
    sortable: true,
    width: 120,
    format: (value: number) => value ? value.toFixed(6) : 'N/A',
  },
  {
    id: 'location_lon',
    label: 'Longitude',
    dataKey: 'location_lon' as keyof Job,
    sortable: true,
    width: 120,
    format: (value: number) => value ? value.toFixed(6) : 'N/A',
  },
  {
    id: 'setup',
    label: 'Setup Time',
    dataKey: 'setup' as keyof Job,
    sortable: true,
    width: 100,
    format: (value: number) => formatDuration(value || 0),
  },
  {
    id: 'service',
    label: 'Service Time',
    dataKey: 'service' as keyof Job,
    sortable: true,
    width: 110,
    format: (value: number) => formatDuration(value || 0),
  },
  {
    id: 'delivery',
    label: 'Delivery',
    dataKey: (row: Job) => 
      Array.isArray(row.delivery) && row.delivery.length > 0
        ? row.delivery.join(', ')
        : 'None',
    width: 120,
    renderCell: (job: Job) => (
      job.delivery && job.delivery.length > 0 ? (
        <Chip
          label={job.delivery.join(', ')}
          size="small"
          color="success"
          variant="outlined"
        />
      ) : (
        <Typography variant="caption" color="text.secondary">None</Typography>
      )
    ),
  },
  {
    id: 'pickup',
    label: 'Pickup',
    dataKey: (row: Job) => 
      Array.isArray(row.pickup) && row.pickup.length > 0
        ? row.pickup.join(', ')
        : 'None',
    width: 120,
    renderCell: (job: Job) => (
      job.pickup && job.pickup.length > 0 ? (
        <Chip
          label={job.pickup.join(', ')}
          size="small"
          color="info"
          variant="outlined"
        />
      ) : (
        <Typography variant="caption" color="text.secondary">None</Typography>
      )
    ),
  },
  {
    id: 'skills',
    label: 'Skills Required',
    dataKey: (row: Job) => 
      row.skills && row.skills.length > 0 
        ? row.skills.join(', ')
        : 'None',
    width: 150,
  },
  {
    id: 'priority',
    label: 'Priority',
    dataKey: 'priority' as keyof Job,
    sortable: true,
    filterable: true,
    width: 100,
    renderCell: (job: Job) => (
      <Chip
        label={job.priority || 0}
        size="small"
        color={job.priority >= 4 ? 'error' : 
               job.priority >= 2 ? 'warning' : 'default'}
        variant="filled"
      />
    ),
  },
  {
    id: 'time_windows',
    label: 'Time Windows',
    dataKey: (row: Job) => {
      if (!row.time_windows || !Array.isArray(row.time_windows) || row.time_windows.length === 0) {
        return 'Anytime';
      }
      
      return row.time_windows.map(tw => {
        // Handle object structure: { start: 28800, end: 64800 }
        let start, end;
        
        if (typeof tw === 'object' && tw !== null) {
          // Object format: { start: number, end: number }
          if ('start' in tw && 'end' in tw) {
            start = tw.start;
            end = tw.end;
          }
          // Array format: [start, end]
          else if (Array.isArray(tw) && tw.length >= 2) {
            start = tw[0];
            end = tw[1];
          }
          else {
            return 'Invalid';
          }
        } else {
          return 'Invalid';
        }
        
        // Check if values are valid numbers
        if (isNaN(start) || isNaN(end) || start === null || end === null || start === undefined || end === undefined) {
          return 'Invalid';
        }
        
        const startHours = Math.floor(start / 3600);
        const startMinutes = Math.floor((start % 3600) / 60);
        const endHours = Math.floor(end / 3600);
        const endMinutes = Math.floor((end % 3600) / 60);
        
        return `${startHours.toString().padStart(2, '0')}:${startMinutes.toString().padStart(2, '0')}-${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
      }).join(', ');
    },
    width: 180,
  },
  {
    id: 'project_id',
    label: 'Project ID',
    dataKey: 'project_id' as keyof Job,
    sortable: true,
    width: 100,
  },
  {
    id: 'created_at',
    label: 'Created',
    dataKey: 'created_at' as keyof Job,
    sortable: true,
    width: 140,
    format: (value: string) => value ? new Date(value).toLocaleDateString() : 'N/A',
  },
  {
    id: 'updated_at',
    label: 'Updated',
    dataKey: 'updated_at' as keyof Job,
    sortable: true,
    width: 140,
    format: (value: string) => value ? new Date(value).toLocaleDateString() : 'N/A',
  },
];

export const DataManagement: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(0);
  // TODO: Replace with Toolpad navigation
  const currentProject = null;
  const { currentDataset, statistics, isLoadingStatistics } = useVRPData();
  const queryClient = useQueryClient();
  
  // Selection is now handled internally by AdvancedDataTable
  
  // Dialog states
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [migrationDialogOpen, setMigrationDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [editingRecord, setEditingRecord] = useState<Location | Vehicle | Job | null>(null);
  const [editData, setEditData] = useState<any>({});  
  const [dataOverviewOpen, setDataOverviewOpen] = useState(false);
  const [optimisationCompatibilityOpen, setOptimisationCompatibilityOpen] = useState(false);
  
  // Error handling
  const [errorInfo, setErrorInfo] = useState<any>(null);
  
  // Entity type for bulk operations
  const [bulkEntityType, setBulkEntityType] = useState<'vehicles' | 'locations' | 'jobs'>('vehicles');
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  
  // Use a default project ID of 1 if no project is selected
  const projectId = currentProject?.id || 1;
  
  // Fetch data with dataset context - prioritize dataset-specific data when available
  const datasetOptions = currentDataset?.id ? {
    dataset_id: currentDataset.id, // CRITICAL: Use dataset_id for filtering dataset-specific data
    scenario_id: currentDataset.scenario_id,
    dataset_name: currentDataset.name,
    dataset_version: currentDataset.version || 1,
    enabled: true
  } : { enabled: true };
  
  console.log('🔍 [DataManagement] Dataset context for data fetching:', {
    hasCurrentDataset: !!currentDataset,
    datasetId: currentDataset?.id,
    scenarioId: currentDataset?.scenario_id,
    datasetName: currentDataset?.name,
    datasetOptions,
    willUseDatasetFiltering: !!currentDataset?.id
  });
  
  const { data: locationsData, isLoading: locationsLoading } = useLocations(
    projectId, 
    currentDataset?.scenario_id, 
    datasetOptions
  );
  const { data: vehiclesData, isLoading: vehiclesLoading } = useVehicles(
    projectId, 
    currentDataset?.scenario_id, 
    datasetOptions
  );
  const { data: jobsData, isLoading: jobsLoading } = useJobs(
    projectId, 
    currentDataset?.scenario_id, 
    datasetOptions
  );
  
  const locations = locationsData || [];
  const vehicles = vehiclesData || [];
  const jobs = jobsData || [];

  // Get current entity data based on tab
  const getCurrentEntityData = () => {
    switch (safeTabValue) {
      case 0: return { data: locations, type: 'locations' as const };
      case 1: return { data: vehicles, type: 'vehicles' as const };
      case 2: return { data: jobs, type: 'jobs' as const };
      default: return { data: [], type: 'locations' as const };
    }
  };

  // Data quality analysis with enhanced metrics - now uses dataset statistics with fallback
  const analyzeDataQuality = () => {
    const analysis = {
      locations: {
        total: statistics?.location_count ?? locations.length,
        withCoordinates: locations.filter(l => l.location_lat && l.location_lon).length,
        withAddresses: locations.filter(l => l.address).length,
        depots: locations.filter(l => l.location_type === 'depot').length,
        customers: locations.filter(l => l.location_type === 'customer').length,
        optimisationReady: locations.filter(l => 
          (l.location_lat && l.location_lon) || l.address
        ).length
      },
      vehicles: {
        total: statistics?.vehicle_count ?? vehicles.length,
        withStartLocation: vehicles.filter(v => 
          (v.start_lat && v.start_lon) || v.start_location_id
        ).length,
        withCapacity: vehicles.filter(v => v.capacity && v.capacity.length > 0).length,
        withTimeWindows: vehicles.filter(v => v.tw_start !== undefined && v.tw_end !== undefined).length,
        optimisationReady: vehicles.filter(v => 
          ((v.start_lat && v.start_lon) || v.start_location_id) && 
          v.capacity && v.capacity.length > 0
        ).length
      },
      jobs: {
        total: statistics?.job_count ?? jobs.length,
        withLocation: jobs.filter(j => 
          (j.location_lat && j.location_lon) || j.location_id
        ).length,
        withTimeWindows: jobs.filter(j => j.time_windows && j.time_windows.length > 0).length,
        withSkills: jobs.filter(j => j.skills && j.skills.length > 0).length,
        optimisationReady: jobs.filter(j => 
          ((j.location_lat && j.location_lon) || j.location_id) &&
          (j.service !== undefined || j.setup !== undefined)
        ).length
      }
    };
    return analysis;
  };

  const dataQuality = analyzeDataQuality();

  // Handle URL parameters for tab switching
  useEffect(() => {
    const tab = searchParams.get('tab');
    
    if (tab === 'locations') {
      setTabValue(0);
    } else if (tab === 'vehicles') {
      setTabValue(1);
    } else if (tab === 'jobs') {
      setTabValue(2);
    }
  }, [searchParams]);

  // Defensive tab value validation to prevent React error #185
  const safeTabValue = React.useMemo(() => {
    if (tabValue === null || tabValue === undefined) {
      console.warn('🔧 [DataManagement] tabValue is null/undefined, defaulting to 0');
      return 0;
    }
    
    const numericValue = Number(tabValue);
    if (isNaN(numericValue) || numericValue < 0 || numericValue > 2 || !Number.isInteger(numericValue)) {
      console.warn('🔧 [DataManagement] Invalid tabValue detected, resetting to 0:', tabValue, 'parsed:', numericValue);
      return 0;
    }
    return numericValue;
  }, [tabValue]);

  const handleTabChange = React.useCallback((event: React.SyntheticEvent, newValue: number) => {
    // Defensive validation to prevent React error #185
    if (!event || event === null) {
      console.warn('⚠️ [DataManagement] Invalid event in handleTabChange:', event);
      return;
    }
    
    // Ensure newValue is a valid number and within range
    const validatedValue = Number(newValue);
    if (isNaN(validatedValue) || validatedValue < 0 || validatedValue > 2) {
      console.warn('⚠️ [DataManagement] Invalid tab value, keeping current:', newValue, 'current:', tabValue);
      return;
    }
    
    console.log('🔄 [DataManagement] Tab change:', { from: tabValue, to: validatedValue });
    
    // Only update if the value actually changed to prevent unnecessary re-renders
    if (validatedValue !== tabValue) {
      setTabValue(validatedValue);
      // Update URL without causing page reload
      const tabNames = ['locations', 'vehicles', 'jobs'];
      setSearchParams({ tab: tabNames[validatedValue] });
    }
  }, [tabValue, setSearchParams]);

  // Enhanced import success handler - now refreshes dataset statistics
  const handleImportSuccess = useCallback(async () => {
    queryClient.invalidateQueries({ queryKey: ['locations', projectId] });
    queryClient.invalidateQueries({ queryKey: ['vehicles', projectId] });
    queryClient.invalidateQueries({ queryKey: ['jobs', projectId] });
    
    // Refresh dataset statistics if we have a current dataset
    if (currentDataset?.id) {
      try {
        // Trigger actual refresh of statistics on the backend
        await apiClient.refreshDatasetStatistics(
          currentDataset.id, 
          currentDataset.name, 
          currentDataset.scenario_id
        );
        // Then invalidate the query cache to refetch the updated statistics
        queryClient.invalidateQueries({ queryKey: ['dataset-statistics', currentDataset.id] });
        console.log('🔄 DataManagement: Refreshed dataset statistics after data import');
      } catch (error) {
        console.error('Failed to refresh dataset statistics:', error);
      }
    }
    
    console.log('🔄 DataManagement: Refreshing data after successful import');
  }, [queryClient, projectId, currentDataset?.id]);

  // Enhanced row click handler - disabled to prevent conflicts with action buttons
  const handleRowClick = (row: Location | Vehicle | Job) => {
    // Disabled row click navigation to prevent conflicts with action buttons
    // Users should use the dedicated action buttons for navigation
    console.log('Row clicked - use action buttons for navigation:', row);
  };

  // Copy/Duplicate record handler
  const handleCopyRecord = (record: Location | Vehicle | Job) => {
    // Create a copy of the record without the ID
    const { id, created_at, updated_at, ...recordData } = record as any;
    
    // Generate unique name with copy suffix
    const generateCopyName = (originalName: string, type: 'name' | 'description'): string => {
      const copyPattern = /\s+\(Copy(\s+\d+)?\)$/;
      const match = originalName.match(copyPattern);
      
      if (match) {
        // Already has copy suffix, increment the number
        const currentNumber = match[1] ? parseInt(match[1].trim()) : 1;
        const baseName = originalName.replace(copyPattern, '');
        return `${baseName} (Copy ${currentNumber + 1})`;
      } else {
        // First copy
        return `${originalName} (Copy)`;
      }
    };
    
    // Update name/description field based on entity type
    if ('location_type' in record) {
      // Location
      recordData.name = generateCopyName(recordData.name || 'Untitled Location', 'name');
      navigate(`/projects/${projectId}/locations/new`, { 
        state: { 
          location: recordData,
          isCopy: true,
          originalId: id
        } 
      });
    } else if ('profile' in record) {
      // Vehicle
      recordData.description = generateCopyName(recordData.description || 'Untitled Vehicle', 'description');
      navigate(`/projects/${projectId}/vehicles/new`, { 
        state: { 
          vehicle: recordData,
          isCopy: true,
          originalId: id
        } 
      });
    } else if ('setup' in record && 'service' in record) {
      // Job
      recordData.description = generateCopyName(recordData.description || 'Untitled Job', 'description');
      navigate(`/projects/${projectId}/jobs/new`, { 
        state: { 
          job: recordData,
          isCopy: true,
          originalId: id
        } 
      });
    }
  };

  // Enhanced bulk delete handler
  const handleBulkDelete = async (selectedRows: (Location | Vehicle | Job)[]) => {
    if (selectedRows.length === 0) {
      setSnackbarMessage('No items selected for deletion');
      setSnackbarOpen(true);
      return;
    }

    // Show confirmation dialog before deleting
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedRows.length} item(s)? This action cannot be undone.`
    );
    
    if (!confirmDelete) return;

    try {
      const deletePromises = selectedRows.map(async (row: any) => {
        try {
          if ('profile' in row) {
            // It's a Vehicle  
            return await apiClient.deleteVehicle(row.id, currentProject?.id || 1);
          } else if ('setup' in row && 'service' in row) {
            // It's a Job
            return await apiClient.deleteJob(row.id, currentProject?.id || 1);
          } else if ('location_type' in row) {
            // It's a Location
            return await apiClient.deleteLocation(row.id, currentProject?.id || 1);
          } else {
            console.error('Unknown row type for deletion:', row);
            throw new Error('Unknown record type');
          }
        } catch (error: any) {
          // Handle individual delete errors
          if (error.message.includes('not found') || error.message.includes('404')) {
            console.warn(`Item with ID ${row.id} not found, may have been already deleted`);
            return null; // Continue with other deletions
          }
          throw error; // Re-throw other errors
        }
      });

      const results = await Promise.allSettled(deletePromises);
      
      // Count successful deletions
      const successful = results.filter(result => 
        result.status === 'fulfilled' && result.value !== null
      ).length;
      
      const failed = results.filter(result => result.status === 'rejected').length;

      if (successful > 0) {
        setSnackbarMessage(
          failed > 0 
            ? `Successfully deleted ${successful} items, ${failed} failed`
            : `Successfully deleted ${successful} items`
        );
      } else {
        setSnackbarMessage('No items were deleted');
      }
      
      setSnackbarOpen(true);
      
      // Refresh data after successful delete (will also refresh statistics)
      await handleImportSuccess();
      
    } catch (error) {
      console.error('Bulk delete error:', error);
      setSnackbarMessage(`Failed to delete items: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setSnackbarOpen(true);
    }
  };

  // Selection is now handled internally by AdvancedDataTable component

  // Enhanced column definitions with actions
  const getLocationColumnsWithActions = () => [
    ...LOCATION_COLUMNS,
    {
      id: 'actions',
      label: 'Actions',
      dataKey: 'id' as keyof Location,
      width: 150,
      align: 'center' as const,
      renderCell: (location: Location) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/projects/${projectId}/locations/${location.id}/edit`, { state: { location } });
              }}
              aria-label={`View details for ${location.name}`}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Location">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/projects/${projectId}/locations/${location.id}/edit`, { state: { location } });
              }}
              aria-label={`Edit ${location.name}`}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy Location">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleCopyRecord(location);
              }}
              sx={{ color: 'info.main' }}
              aria-label={`Copy ${location.name}`}
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Location">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`Are you sure you want to delete location "${location.name}"?`)) {
                  handleBulkDelete([location]);
                }
              }}
              sx={{ color: 'error.main' }}
              aria-label={`Delete ${location.name}`}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const getVehicleColumnsWithActions = () => [
    ...VEHICLE_COLUMNS,
    {
      id: 'actions',
      label: 'Actions',
      dataKey: 'id' as keyof Vehicle,
      width: 150,
      align: 'center' as const,
      renderCell: (vehicle: Vehicle) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/projects/${projectId}/vehicles/${vehicle.id}/edit`, { state: { vehicle } });
              }}
              aria-label={`View details for ${vehicle.description}`}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Vehicle">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/projects/${projectId}/vehicles/${vehicle.id}/edit`, { state: { vehicle } });
              }}
              aria-label={`Edit ${vehicle.description}`}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy Vehicle">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleCopyRecord(vehicle);
              }}
              sx={{ color: 'info.main' }}
              aria-label={`Copy ${vehicle.description}`}
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Vehicle">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`Are you sure you want to delete vehicle "${vehicle.description}"?`)) {
                  handleBulkDelete([vehicle]);
                }
              }}
              sx={{ color: 'error.main' }}
              aria-label={`Delete ${vehicle.description}`}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const getJobColumnsWithActions = () => [
    ...JOB_COLUMNS,
    {
      id: 'actions',
      label: 'Actions',
      dataKey: 'id' as keyof Job,
      width: 150,
      align: 'center' as const,
      renderCell: (job: Job) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/projects/${projectId}/jobs/${job.id}/edit`, { state: { job } });
              }}
              aria-label={`View details for ${job.description}`}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Job">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/projects/${projectId}/jobs/${job.id}/edit`, { state: { job } });
              }}
              aria-label={`Edit ${job.description}`}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy Job">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleCopyRecord(job);
              }}
              sx={{ color: 'info.main' }}
              aria-label={`Copy ${job.description}`}
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Job">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`Are you sure you want to delete job "${job.description}"?`)) {
                  handleBulkDelete([job]);
                }
              }}
              sx={{ color: 'error.main' }}
              aria-label={`Delete ${job.description}`}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // Show warning if no project is selected
  const showProjectWarning = !currentProject;

  // Enhanced tab data with dataset-specific counts
  const tabData = [
    {
      label: 'Locations',
      icon: LocationOn,
      count: statistics?.location_count ?? locations.length,
      color: theme.secondary,
      loading: isLoadingStatistics || locationsLoading,
    },
    {
      label: 'Vehicles', 
      icon: DirectionsCar,
      count: statistics?.vehicle_count ?? vehicles.length,
      color: theme.primary,
      loading: isLoadingStatistics || vehiclesLoading,
    },
    {
      label: 'Jobs',
      icon: Work,
      count: statistics?.job_count ?? jobs.length,
      color: theme.accent,
      loading: isLoadingStatistics || jobsLoading,
    },
  ];

  // Basic handlers for creating scenarios and datasets
  const handleCreateScenario = () => {
    // Navigate to scenario creation page
    navigate('/scenarios/new');
  };

  const handleCreateDataset = () => {
    // Navigate to dataset creation page
    navigate('/scenarios');
  };

  return (
    <ProfessionalLayout
      pageTitle="Data Management"
      pageSubtitle="Manage your VRP data: locations, vehicles, and jobs"
      activeTab="Data Management"
      actionButtons={
        <>
          <Button 
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => setImportDialogOpen(true)}
            sx={{ mr: 1 }}
          >
            Import Data
          </Button>
          <Button 
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              // Navigate to the appropriate "add" form based on current tab
              const tabNames = ['locations', 'vehicles', 'jobs'];
              navigate(`/projects/${projectId}/${tabNames[safeTabValue]}/new`);
            }}
          >
            Add {['Location', 'Vehicle', 'Job'][safeTabValue]}
          </Button>
        </>
      }
    >
      <Container maxWidth="xl" sx={{ py: 0, mt: -4.5 }}>
        <Box sx={{ width: '100%' }}>
        

        {showProjectWarning && (
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 2,
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" fontWeight="500">
              No project selected. Please select a project to view and manage data.
            </Typography>
          </Alert>
        )}

        {!currentDataset && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 2,
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" fontWeight="500">
              No dataset selected. Data counts show project-level totals. Select a dataset for precise dataset-specific counts.
            </Typography>
          </Alert>
        )}
        

        {/* Enhanced Tabs Section */}
        <Paper 
          elevation={0} 
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: theme.shadows.card,
            background: theme.surface,
          }}
        >
          {/* Sophisticated Tab Bar */}
          <Box sx={{ 
            background: theme.surfaceVariant,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <Tabs 
              value={safeTabValue} 
              onChange={handleTabChange} 
              aria-label="enhanced data management tabs"
              sx={{
                px: 2,
                pt: 1,
                minHeight: 48,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  minHeight: 44,
                  borderRadius: '8px 8px 0 0',
                  marginRight: 0.5,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&.Mui-selected': {
                    color: theme.primary,
                    background: theme.surface,
                    boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.06)',
                  },
                  '&:hover:not(.Mui-selected)': {
                    background: 'rgba(25, 118, 210, 0.04)',
                    transform: 'translateY(-1px)',
                  },
                },
                '& .MuiTabs-indicator': {
                  height: 0, // Hide default indicator since we use custom styling
                },
              }}
            >
                {tabData.map((tab, index) => (
                  <Tab 
                    key={tab.label}
                    value={index}
                    icon={
                      <Badge 
                        badgeContent={tab.count} 
                        color="primary" 
                        sx={{
                          '& .MuiBadge-badge': {
                            backgroundColor: tab.color,
                            color: 'white',
                            fontWeight: 600,
                          }
                        }}
                      >
                        <tab.icon />
                      </Badge>
                    } 
                    label={tab.label} 
                    iconPosition="start" 
                    {...a11yProps(index)} 
                  />
                ))}
              </Tabs>
              
              {/* Quick Actions Icons inline with tabs */}
              <Stack direction="row" spacing={0.5} sx={{ pr: 2 }}>
                <Tooltip title="View comprehensive data analytics and quality metrics for optimisation readiness">
                  <IconButton 
                    size="small"
                    sx={{ 
                      color: theme.primary,
                      width: 32,
                      height: 32,
                      '&:hover': { bgcolor: `${theme.primary}10` }
                    }}
                    onClick={() => setDataOverviewOpen(true)}
                  >
                    <InsightsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                
                {/* Progressive disclosure for optimisation compatibility check */}
                {(locations.length + vehicles.length + jobs.length) >= 10 && (
                  <Tooltip title="Check optimisation compatibility - ensure all data meets vehicle routing optimization requirements">
                    <IconButton 
                      size="small"
                      sx={{ 
                        color: theme.secondary,
                        width: 32,
                        height: 32,
                        '&:hover': { bgcolor: `${theme.secondary}10` }
                      }}
                      onClick={() => setOptimisationCompatibilityOpen(true)}
                    >
                      <SpeedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                
                {/* Progressive disclosure for data migration */}
                {currentDataset && (locations.length + vehicles.length + jobs.length) >= 20 && (
                  <Tooltip title="Copy or migrate dataset to another scenario for comparison analysis">
                    <IconButton 
                      size="small"
                      sx={{ 
                        color: theme.accent,
                        width: 32,
                        height: 32,
                        '&:hover': { bgcolor: `${theme.accent}10` }
                      }}
                      onClick={() => setMigrationDialogOpen(true)}
                    >
                      <Upload fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                
                <GuidedWorkflowTooltips
                  context="data-management"
                  showProgress={false}
                  onSuggestAction={(action) => {
                    if (action === 'import-data') {
                      setImportDialogOpen(true);
                    } else if (action === 'optimisation-compatibility') {
                      setOptimisationCompatibilityOpen(true);
                    }
                  }}
                />
              </Stack>
            </Box>

            {/* Enhanced Tab Content */}
            <Box sx={{ minHeight: '65vh' }}>
              {/* Locations Tab */}
              <TabPanel value={safeTabValue} index={0}>
                <AdvancedDataTable
                  columns={getLocationColumnsWithActions()}
                  data={locations}
                  loading={locationsLoading}
                  title=""
                  keyField="id"
                  onBulkDelete={handleBulkDelete}
                  enableExport
                  enableSelection
                  enableFiltering
                  enableSorting
                  enableColumnVisibility
                  defaultPageSize={25}
                  pageSizeOptions={[10, 25, 50, 100, 200]}
                  maxHeight="75vh"
                  customActions={[
                    {
                      label: 'Add',
                      icon: <Add />,
                      action: () => navigate(`/projects/${projectId}/locations/new`),
                      disabled: () => false,
                      requiresSelection: false
                    },
                    {
                      label: 'Import',
                      icon: <Upload />,
                      action: () => {
                        setBulkEntityType('locations');
                        setImportDialogOpen(true);
                      },
                      disabled: () => false,
                      requiresSelection: false
                    },
                    {
                      label: 'Export',
                      icon: <Download />,
                      action: () => {
                        setBulkEntityType('locations');
                        setExportDialogOpen(true);
                      },
                      disabled: () => false,
                      requiresSelection: false
                    }
                  ]}
                />
              </TabPanel>
              
              {/* Vehicles Tab */}
              <TabPanel value={safeTabValue} index={1}>
                <AdvancedDataTable
                  columns={getVehicleColumnsWithActions()}
                  data={vehicles}
                  loading={vehiclesLoading}
                  title=""
                  keyField="id"
                  onBulkDelete={handleBulkDelete}
                  enableExport
                  enableSelection
                  enableFiltering
                  enableSorting
                  enableColumnVisibility
                  defaultPageSize={25}
                  pageSizeOptions={[10, 25, 50, 100, 200]}
                  maxHeight="75vh"
                  customActions={[
                    {
                      label: 'Add',
                      icon: <Add />,
                      action: () => navigate(`/projects/${projectId}/vehicles/new`),
                      disabled: () => false,
                      requiresSelection: false
                    },
                    {
                      label: 'Import',
                      icon: <Upload />,
                      action: () => {
                        setBulkEntityType('vehicles');
                        setImportDialogOpen(true);
                      },
                      disabled: () => false,
                      requiresSelection: false
                    },
                    {
                      label: 'Export',
                      icon: <Download />,
                      action: () => {
                        setBulkEntityType('vehicles');
                        setExportDialogOpen(true);
                      },
                      disabled: () => false,
                      requiresSelection: false
                    }
                  ]}
                />
              </TabPanel>
              
              {/* Jobs Tab */}
              <TabPanel value={safeTabValue} index={2}>
                <AdvancedDataTable
                  columns={getJobColumnsWithActions()}
                  data={jobs}
                  loading={jobsLoading}
                  title=""
                  keyField="id"
                  onBulkDelete={handleBulkDelete}
                  enableExport
                  enableSelection
                  enableFiltering
                  enableSorting
                  enableColumnVisibility
                  defaultPageSize={25}
                  pageSizeOptions={[10, 25, 50, 100, 200]}
                  maxHeight="75vh"
                  customActions={[
                    {
                      label: 'Add',
                      icon: <Add />,
                      action: () => navigate(`/projects/${projectId}/jobs/new`),
                      disabled: () => false,
                      requiresSelection: false
                    },
                    {
                      label: 'Import',
                      icon: <Upload />,
                      action: () => {
                        setBulkEntityType('jobs');
                        setImportDialogOpen(true);
                      },
                      disabled: () => false,
                      requiresSelection: false
                    },
                    {
                      label: 'Export',
                      icon: <Download />,
                      action: () => {
                        setBulkEntityType('jobs');
                        setExportDialogOpen(true);
                      },
                      disabled: () => false,
                      requiresSelection: false
                    }
                  ]}
                />
              </TabPanel>
            </Box>
          </Paper>

        {/* Enhanced Bulk Import Dialog */}
        <BulkImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          onSuccess={handleImportSuccess}
          entityType={bulkEntityType}
          projectId={projectId}
          availableLocations={locations.map(l => ({ id: l.id, name: l.name }))}
        />

        {/* Enhanced Bulk Export Dialog */}
        <BulkExportDialog
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          entityType={bulkEntityType}
          data={getCurrentEntityData().data}
          projectName={currentProject?.name || 'VRP_Project'}
        />

        {/* Data Migration Dialog */}
        <DataMigrationDialog
          open={migrationDialogOpen}
          onClose={() => setMigrationDialogOpen(false)}
          sourceDataset={currentDataset}
          onSuccess={(newDataset) => {
            setSnackbarMessage(`Successfully migrated dataset: ${newDataset.name}`);
            setSnackbarOpen(true);
            handleImportSuccess();
          }}
        />

        {/* Enhanced Error Handler */}
        {errorInfo && (
          <Box sx={{ mb: 2 }}>
            <EnhancedErrorHandler
              error={errorInfo}
              onRetry={() => {
                setErrorInfo(null);
                // Could add retry logic here
              }}
              onDismiss={() => setErrorInfo(null)}
            />
          </Box>
        )}

        {/* Enhanced Data Overview Dialog */}
        <Dialog 
          open={dataOverviewOpen} 
          onClose={() => setDataOverviewOpen(false)} 
          maxWidth="lg" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              boxShadow: theme.shadows.float,
            }
          }}
        >
          <DialogTitle sx={{ 
            background: theme.gradients.primary,
            color: 'white',
            borderRadius: '16px 16px 0 0',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
                <AnalyticsIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="700">
                  Data Analytics & Quality Overview
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Comprehensive analysis of your VRP data quality and optimisation readiness
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 4 }}>
            <Grid container spacing={4}>
              {/* Enhanced Data Quality Cards */}
              {[
                { 
                  title: 'Locations', 
                  icon: LocationOn, 
                  data: dataQuality.locations, 
                  color: theme.secondary,
                  gradient: theme.gradients.secondary 
                },
                { 
                  title: 'Vehicles', 
                  icon: DirectionsCar, 
                  data: dataQuality.vehicles, 
                  color: theme.primary,
                  gradient: theme.gradients.primary 
                },
                { 
                  title: 'Jobs', 
                  icon: Work, 
                  data: dataQuality.jobs, 
                  color: theme.accent,
                  gradient: theme.gradients.accent 
                },
              ].map((section, index) => (
                <Grid size={{ xs: 12, md: 4 }} key={section.title}>
                  <Zoom in timeout={300 + index * 200}>
                    <Card sx={{ 
                      height: '100%',
                      borderRadius: 3,
                      boxShadow: theme.shadows.card,
                      overflow: 'visible',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows.elevated,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }
                    }}>
                      <Box sx={{
                        background: section.gradient,
                        color: 'white',
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                      }}>
                        <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
                          <section.icon />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight="700">
                            {section.title}
                          </Typography>
                          <Typography variant="h4" fontWeight="700">
                            {section.data.total}
                          </Typography>
                        </Box>
                      </Box>
                      <CardContent sx={{ p: 3 }}>
                        <Stack spacing={2}>
                          {Object.entries(section.data).filter(([key]) => key !== 'total').map(([key, value]) => (
                            <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                              </Typography>
                              <Chip 
                                label={value as number} 
                                size="small" 
                                color={key === 'optimisationReady' ? 'success' : 'default'}
                                variant={key === 'optimisationReady' ? 'filled' : 'outlined'}
                              />
                            </Box>
                          ))}
                          <Divider />
                          <Box>
                            <Typography variant="body2" fontWeight="600" gutterBottom>
                              Optimisation Readiness
                            </Typography>
                            <LinearProgress 
                              variant="determinate" 
                              value={(section.data.optimisationReady / Math.max(section.data.total, 1)) * 100}
                              sx={{ 
                                height: 8, 
                                borderRadius: 4,
                                bgcolor: 'rgba(0, 0, 0, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                  background: section.gradient,
                                  borderRadius: 4,
                                }
                              }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              {Math.round((section.data.optimisationReady / Math.max(section.data.total, 1)) * 100)}% ready
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={() => setDataOverviewOpen(false)} size="large">
              Close
            </Button>
            <Button 
              variant="contained" 
              startIcon={<SpeedIcon />} 
              onClick={() => {
                setDataOverviewOpen(false);
                setOptimisationCompatibilityOpen(true);
              }}
              sx={{ background: theme.gradients.primary }}
              size="large"
            >
              Check Optimisation Compatibility
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        />
        </Box>
      </Container>
    </ProfessionalLayout>
  );
};